import React, { useEffect, useState } from 'react';
import { CalendarIcon, UserIcon, ClockIcon, ExclamationCircleIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../components/ui/use-toast';
import { StatsSkeleton, ChartSkeleton, LeadListSkeleton } from '../components/ui/skeleton';
import DashboardCharts from '../components/dashboard/DashboardCharts';
import { useUserProfile } from '../hooks/useUserProfile';
import apiService from '../services/apiService';

/**
 * Interface para estatísticas do dashboard
 */
interface DashboardStats {
  totalLeads: number;
  totalAppointments: number;
  totalDoctors: number;
  totalClinics: number;
  newLeadsThisMonth: number;
  appointmentsThisWeek: number;
  averageConversionTime: number; // em dias
  leadsByStage: {
    acolhimento: number;
    qualificacao: number;
    educacao: number;
    objecoes: number;
    proposta: number;
    agendamento: number;
    followup: number;
    recuperacao: number;
    outros: number;
  };
  appointmentsByStatus: {
    agendado: number;
    confirmado: number;
    realizado: number;
    cancelado: number;
    remarcado: number;
  };
  upcomingAppointments: any[];
  recentLeads: any[];
}

/**
 * Componente da página Dashboard
 * @author Matheus
 * @returns JSX.Element
 */
const Dashboard: React.FC = () => {
  const { userProfile, loading: profileLoading } = useUserProfile();
  const [loading, setLoading] = useState<boolean>(true);
  const { toast } = useToast();
  const [stats, setStats] = useState<DashboardStats>({
    totalLeads: 0,
    totalAppointments: 0,
    totalDoctors: 0,
    totalClinics: 0,
    newLeadsThisMonth: 0,
    appointmentsThisWeek: 0,
    averageConversionTime: 0,
    leadsByStage: {
      acolhimento: 0,
      qualificacao: 0,
      educacao: 0,
      objecoes: 0,
      proposta: 0,
      agendamento: 0,
      followup: 0,
      recuperacao: 0,
      outros: 0
    },
    appointmentsByStatus: {
      agendado: 0,
      confirmado: 0,
      realizado: 0,
      cancelado: 0,
      remarcado: 0
    },
    upcomingAppointments: [],
    recentLeads: []
  });
  
  const [weeklyTrendData, setWeeklyTrendData] = useState<{
    day: string;
    leads: number;
    appointments: number;
  }[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Processar dados de tendência semanal dos dados recebidos
  const processWeeklyTrendData = (leads: any[], appointments: any[]) => {
    const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const today = new Date();
    const weeklyData = [];

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayName = weekDays[date.getDay()];

      // Contar leads e appointments para este dia
      const leadsCount = leads?.filter((lead: any) => 
        lead.created_at?.startsWith(dateStr)
      ).length || 0;
      
      const appointmentsCount = appointments?.filter((appointment: any) => 
        appointment.created_at?.startsWith(dateStr)
      ).length || 0;

      weeklyData.push({
        day: dayName,
        leads: leadsCount,
        appointments: appointmentsCount
      });
    }

    return weeklyData;
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Aguardar o carregamento do perfil do usuário
        if (profileLoading) {
          return;
        }
        
        setLoading(true);
        setError(null);
        
        if (!userProfile?.clinic_id) {
          setLoading(false);
          return;
        }
        
        // Buscar métricas via API
        const response = await apiService.getDashboardMetrics();
        
        if (!response.sucesso || !response.dados) {
          throw new Error('Erro ao buscar métricas do dashboard');
        }
        
        const metrics = response.dados as any;
        const allLeads = metrics.leads || [];
        const allAppointments = metrics.agendamentos || [];
        
        // Calcular dados derivados
        const currentDate = new Date();
        const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const today = new Date();
        const firstDayOfWeek = new Date(today);
        firstDayOfWeek.setDate(today.getDate() - today.getDay());
        firstDayOfWeek.setHours(0, 0, 0, 0);
        
        const newLeadsThisMonth = allLeads.filter((lead: any) => 
          new Date(lead.created_at) >= firstDayOfMonth
        ).length;
        
        const appointmentsThisWeek = allAppointments.filter((apt: any) => 
          new Date(apt.date || apt.created_at) >= firstDayOfWeek
        ).length;
        
        // Calcular leads por estágio
        const leadsByStageCount = {
          acolhimento: 0,
          qualificacao: 0,
          educacao: 0,
          objecoes: 0,
          proposta: 0,
          agendamento: 0,
          followup: 0,
          recuperacao: 0,
          outros: 0
        };

        allLeads.forEach((lead: any) => {
          const stage = lead.stage || 'outros';
          if (stage in leadsByStageCount) {
            leadsByStageCount[stage as keyof typeof leadsByStageCount] += 1;
          } else {
            leadsByStageCount.outros += 1;
          }
        });

        // Calcular agendamentos por status
        const appointmentsByStatusCount = {
          agendado: 0,
          confirmado: 0,
          realizado: 0,
          cancelado: 0,
          remarcado: 0
        };

        allAppointments.forEach((appointment: any) => {
          const status = appointment.status || 'agendado';
          if (status in appointmentsByStatusCount) {
            appointmentsByStatusCount[status as keyof typeof appointmentsByStatusCount] += 1;
          }
        });

        // Próximos agendamentos (próximos 5)
        const upcomingAppointments = allAppointments
          .filter((apt: any) => new Date(apt.date) >= new Date())
          .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
          .slice(0, 5);
        
        // Últimos 5 leads
        const recentLeads = allLeads
          .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, 5);
        
        // Calcular tempo médio de conversão
        const conversionsData = allAppointments.filter((apt: any) => 
          apt.status === 'realizado' && apt.lead_id
        );
        let averageConversionTime = 0;
        if (conversionsData.length > 0) {
          const conversionTimes: number[] = [];
          conversionsData.forEach((appointment: any) => {
            const lead = allLeads.find((l: any) => l.id === appointment.lead_id);
            if (lead && lead.created_at && appointment.date) {
              const leadCreatedAt = new Date(lead.created_at);
              const appointmentDate = new Date(appointment.date);
              const diffTime = appointmentDate.getTime() - leadCreatedAt.getTime();
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              if (diffDays >= 0) {
                conversionTimes.push(diffDays);
              }
            }
          });
          if (conversionTimes.length > 0) {
            averageConversionTime = Math.round(
              conversionTimes.reduce((sum, time) => sum + time, 0) / conversionTimes.length
            );
          }
        }
        
        // Processar dados semanais
        const weeklyData = processWeeklyTrendData(allLeads, allAppointments);
        setWeeklyTrendData(weeklyData);
        
        // Atualizar estado
        setStats({
          totalLeads: metrics.totalLeads || 0,
          totalAppointments: metrics.totalAgendamentos || 0,
          totalDoctors: metrics.totalDoutores || 0,
          totalClinics: 1,
          newLeadsThisMonth,
          appointmentsThisWeek,
          averageConversionTime,
          leadsByStage: leadsByStageCount,
          appointmentsByStatus: appointmentsByStatusCount,
          upcomingAppointments,
          recentLeads
        });

      } catch (err: any) {
        // Erro silencioso
        toast({
          title: "Erro ao carregar dados",
          description: `Ocorreu um erro ao carregar os dados: ${err.message || 'Erro desconhecido'}`,
          variant: "destructive",
        });
        setError(`Ocorreu um erro ao carregar os dados: ${err.message || 'Erro desconhecido'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userProfile, profileLoading, toast]);

  // Função para formatar a data para o formato brasileiro (DD/MM/YYYY) com dia da semana
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }).replace('.', '');
    } catch (error) {
      // Erro silencioso
      return dateString || '-';
    }
  };



  // Função auxiliar para formatar o WhatsApp
  const formatWhatsApp = (whatsapp: string): string => {
    // Remove caracteres não numéricos
    const numbers = whatsapp.replace(/\D/g, '');
    
    // Se for um número brasileiro padrão (com 55 no início)
    if (numbers.startsWith('55') && numbers.length === 13) {
      const ddd = numbers.substring(2, 4);
      const part1 = numbers.substring(4, 9);
      const part2 = numbers.substring(9);
      return `(${ddd}) ${part1}-${part2}`;
    }
    
    return whatsapp;
  };

  return (
    <div className="p-5 md:p-7 animate-fade-in space-y-7 overflow-y-auto">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-7 gap-4 bg-card p-6 rounded-xl border border-border">
        <div className="flex items-center">
          <div className="bg-primary p-3 rounded-xl mr-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-6 w-6 text-white">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1 font-medium">Visão geral da sua clínica odontológica</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2 bg-primary px-4 py-3 rounded-xl border border-border/20">
            <CalendarIcon className="h-5 w-5 text-white" />
            <span className="text-sm font-medium text-white">
              {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="space-y-6">
          <StatsSkeleton />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartSkeleton />
              <ChartSkeleton />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <LeadListSkeleton />
              <LeadListSkeleton />
            </div>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border-l-4 border-destructive p-4 animate-fade-in rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-destructive" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-destructive">
                {error}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Cartões de estatísticas */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            {/* Card Total de Leads */}
            <Card className="border border-border transition-all duration-300">
              <CardHeader className="bg-accent border-b border-border flex flex-row items-center justify-between space-y-0 pb-3">
                <div className="flex items-center">
                  <div className="bg-primary h-10 w-10 rounded-xl shadow-lg flex items-center justify-center mr-3">
                    <UserIcon className="h-5 w-5 text-white" />
                  </div>
                  <CardTitle className="text-lg font-bold text-foreground">Total de Leads</CardTitle>
                </div>

              </CardHeader>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-4xl font-bold text-foreground">{stats.totalLeads}</p>
                    <p className="text-sm text-muted-foreground mt-1 font-medium">Leads cadastrados</p>
                  </div>
                  <div className="bg-primary h-16 w-16 rounded-xl flex items-center justify-center">
                    <UserPlusIcon className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div className="flex items-center pt-4 border-t border-border">
                  <Badge variant="secondary" className="bg-secondary text-secondary-foreground">
                    <span className="inline-flex h-2 w-2 rounded-full bg-primary mr-2"></span>
                    {stats.newLeadsThisMonth}
                  </Badge>
                  <span className="ml-3 text-sm text-muted-foreground font-medium">novos este mês</span>
                </div>
              </CardContent>
            </Card>

            {/* Card Agendamentos */}
            <Card className="border border-border transition-all duration-300">
              <CardHeader className="bg-accent border-b border-border flex flex-row items-center justify-between space-y-0 pb-3">
                <div className="flex items-center">
                  <div className="bg-primary h-10 w-10 rounded-xl shadow-lg flex items-center justify-center mr-3">
                    <CalendarIcon className="h-5 w-5 text-white" />
                  </div>
                  <CardTitle className="text-lg font-bold text-foreground">Agendamentos</CardTitle>
                </div>

              </CardHeader>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-4xl font-bold text-foreground">{stats.totalAppointments}</p>
                    <p className="text-sm text-muted-foreground mt-1 font-medium">Total de consultas</p>
                  </div>
                  <div className="bg-primary h-16 w-16 rounded-xl flex items-center justify-center">
                    <CalendarIcon className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div className="flex items-center pt-4 border-t border-border">
                  <Badge variant="secondary" className="bg-secondary text-secondary-foreground">
                    <span className="inline-flex h-2 w-2 rounded-full bg-primary mr-2"></span>
                    {stats.appointmentsThisWeek}
                  </Badge>
                  <span className="ml-3 text-sm text-muted-foreground font-medium">nesta semana</span>
                </div>
              </CardContent>
            </Card>

            {/* Card Tempo Médio de Conversão */}
            <Card className="border border-border transition-all duration-300">
              <CardHeader className="bg-accent border-b border-border flex flex-row items-center justify-between space-y-0 pb-3">
                <div className="flex items-center">
                  <div className="bg-primary h-10 w-10 rounded-xl shadow-lg flex items-center justify-center mr-3">
                    <ClockIcon className="h-5 w-5 text-white" />
                  </div>
                  <CardTitle className="text-lg font-bold text-foreground">Tempo Médio de Conversão</CardTitle>
                </div>

              </CardHeader>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-4xl font-bold text-foreground">
                      {stats.averageConversionTime}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1 font-medium">
                      {stats.averageConversionTime === 1 ? 'dia' : 'dias'} para conversão
                    </p>
                  </div>
                  <div className="bg-primary h-16 w-16 rounded-xl flex items-center justify-center">
                    <ClockIcon className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div className="flex items-center pt-4 border-t border-border">
                  <Badge variant="secondary" className="bg-secondary text-secondary-foreground">
                    <span className="inline-flex h-2 w-2 rounded-full bg-primary mr-2"></span>
                    {stats.appointmentsByStatus.realizado}
                  </Badge>
                  <span className="ml-3 text-sm text-muted-foreground font-medium">agendamentos realizados</span>
                </div>
              </CardContent>
            </Card>

            {/* Card Doutores */}
            <Card className="border border-border transition-all duration-300">
              <CardHeader className="bg-accent border-b border-border flex flex-row items-center justify-between space-y-0 pb-3">
                <div className="flex items-center">
                  <div className="bg-primary h-10 w-10 rounded-xl shadow-lg flex items-center justify-center mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                    </svg>
                  </div>
                  <CardTitle className="text-lg font-bold text-foreground">Doutores</CardTitle>
                </div>

              </CardHeader>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-4xl font-bold text-foreground">{stats.totalDoctors}</p>
                    <p className="text-sm text-muted-foreground mt-1 font-medium">Profissionais cadastrados</p>
                  </div>
                  <div className="bg-primary h-16 w-16 rounded-xl flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 01-1.125-1.125v-3.75zM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-8.25zM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-2.25z" />
                    </svg>
                  </div>
                </div>
                <div className="flex items-center pt-4 border-t border-border">
                  <Badge variant="secondary" className="bg-secondary text-secondary-foreground">
                    <span className="inline-flex h-2 w-2 rounded-full bg-primary mr-2"></span>
                    {stats.totalClinics}
                  </Badge>
                  <span className="ml-3 text-sm text-muted-foreground font-medium">clínica ativa</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Gráficos e Dados de Leads */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            <Card className="lg:col-span-2 border border-border transition-all duration-300">
              <CardHeader className="bg-accent border-b border-border flex flex-row items-center justify-between space-y-0 pb-4">
                <div className="flex items-center">
                  <div className="bg-primary h-10 w-10 rounded-xl shadow-lg flex items-center justify-center mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                    </svg>
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold text-foreground">Distribuição de Leads</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1 font-medium">Análise detalhada por estágio</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-8">
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                <div className="flex flex-col items-center justify-center p-4 rounded-2xl bg-accent border-2 border-border shadow hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
                  <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="bg-primary p-2 rounded-full mb-2 transition-all duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-foreground group-hover:text-foreground/80 transition-colors duration-300 text-center">Acolhimento</span>
                  <span className="text-2xl font-bold text-foreground my-2 group-hover:scale-110 transition-transform duration-300">{stats.leadsByStage.acolhimento}</span>
                  <div className="w-full bg-muted rounded-full h-2 mt-1 overflow-hidden">
                    <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.max(5, (stats.leadsByStage.acolhimento / stats.totalLeads) * 100)}%` }}></div>
                  </div>
                  <span className="text-xs font-medium text-foreground mt-1 bg-muted px-2 py-1 rounded-full">{((stats.leadsByStage.acolhimento / stats.totalLeads) * 100).toFixed(1)}%</span>
                </div>
                
                <div className="flex flex-col items-center justify-center p-4 rounded-2xl bg-accent border-2 border-border shadow hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
                  <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="bg-primary p-2 rounded-full mb-2 transition-all duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-foreground group-hover:text-foreground/80 transition-colors duration-300 text-center">Qualificação</span>
                  <span className="text-2xl font-bold text-foreground my-2 group-hover:scale-110 transition-transform duration-300">{stats.leadsByStage.qualificacao}</span>
                  <div className="w-full bg-muted rounded-full h-2 mt-1 overflow-hidden">
                    <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.max(5, (stats.leadsByStage.qualificacao / stats.totalLeads) * 100)}%` }}></div>
                  </div>
                  <span className="text-xs font-medium text-foreground mt-1 bg-muted px-2 py-1 rounded-full">{((stats.leadsByStage.qualificacao / stats.totalLeads) * 100).toFixed(1)}%</span>
                </div>
                
                <div className="flex flex-col items-center justify-center p-4 rounded-2xl bg-accent border-2 border-border shadow hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
                  <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="bg-primary p-2 rounded-full mb-2 transition-all duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-foreground group-hover:text-foreground/80 transition-colors duration-300 text-center">Educação</span>
                  <span className="text-2xl font-bold text-foreground my-2 group-hover:scale-110 transition-transform duration-300">{stats.leadsByStage.educacao}</span>
                  <div className="w-full bg-muted rounded-full h-2 mt-1 overflow-hidden">
                    <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.max(5, (stats.leadsByStage.educacao / stats.totalLeads) * 100)}%` }}></div>
                  </div>
                  <span className="text-xs font-medium text-foreground mt-1 bg-muted px-2 py-1 rounded-full">{((stats.leadsByStage.educacao / stats.totalLeads) * 100).toFixed(1)}%</span>
                </div>
                
                <div className="flex flex-col items-center justify-center p-4 rounded-2xl bg-accent border-2 border-border shadow hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
                  <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="bg-primary p-2 rounded-full mb-2 transition-all duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-foreground group-hover:text-foreground/80 transition-colors duration-300 text-center">Objeções</span>
                  <span className="text-2xl font-bold text-foreground my-2 group-hover:scale-110 transition-transform duration-300">{stats.leadsByStage.objecoes}</span>
                  <div className="w-full bg-muted rounded-full h-2 mt-1 overflow-hidden">
                    <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.max(5, (stats.leadsByStage.objecoes / stats.totalLeads) * 100)}%` }}></div>
                  </div>
                  <span className="text-xs font-medium text-foreground mt-1 bg-muted px-2 py-1 rounded-full">{((stats.leadsByStage.objecoes / stats.totalLeads) * 100).toFixed(1)}%</span>
                </div>
                
                <div className="flex flex-col items-center justify-center p-4 rounded-2xl bg-accent border-2 border-border shadow hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
                  <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="bg-primary p-2 rounded-full mb-2 transition-all duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-foreground group-hover:text-foreground/80 transition-colors duration-300 text-center">Proposta</span>
                  <span className="text-2xl font-bold text-foreground my-2 group-hover:scale-110 transition-transform duration-300">{stats.leadsByStage.proposta}</span>
                  <div className="w-full bg-muted rounded-full h-2 mt-1 overflow-hidden">
                    <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.max(5, (stats.leadsByStage.proposta / stats.totalLeads) * 100)}%` }}></div>
                  </div>
                  <span className="text-xs font-medium text-foreground mt-1 bg-muted px-2 py-1 rounded-full">{((stats.leadsByStage.proposta / stats.totalLeads) * 100).toFixed(1)}%</span>
                </div>
                
                <div className="flex flex-col items-center justify-center p-4 rounded-2xl bg-accent border-2 border-border shadow hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
                  <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="bg-primary p-2 rounded-full mb-2 transition-all duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0121 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-foreground group-hover:text-foreground/80 transition-colors duration-300 text-center">Agendamento</span>
                  <span className="text-2xl font-bold text-foreground my-2 group-hover:scale-110 transition-transform duration-300">{stats.leadsByStage.agendamento}</span>
                  <div className="w-full bg-muted rounded-full h-2 mt-1 overflow-hidden">
                    <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.max(5, (stats.leadsByStage.agendamento / stats.totalLeads) * 100)}%` }}></div>
                  </div>
                  <span className="text-xs font-medium text-foreground mt-1 bg-muted px-2 py-1 rounded-full">{((stats.leadsByStage.agendamento / stats.totalLeads) * 100).toFixed(1)}%</span>
                </div>
                
                <div className="flex flex-col items-center justify-center p-4 rounded-2xl bg-accent border-2 border-border shadow hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
                  <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="bg-primary p-2 rounded-full mb-2 transition-all duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-foreground group-hover:text-foreground/80 transition-colors duration-300 text-center">Follow-up</span>
                  <span className="text-2xl font-bold text-foreground my-2 group-hover:scale-110 transition-transform duration-300">{stats.leadsByStage.followup}</span>
                  <div className="w-full bg-muted rounded-full h-2 mt-1 overflow-hidden">
                    <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.max(5, (stats.leadsByStage.followup / stats.totalLeads) * 100)}%` }}></div>
                  </div>
                  <span className="text-xs font-medium text-foreground mt-1 bg-muted px-2 py-1 rounded-full">{((stats.leadsByStage.followup / stats.totalLeads) * 100).toFixed(1)}%</span>
                </div>
                
                <div className="flex flex-col items-center justify-center p-4 rounded-2xl bg-accent border-2 border-border shadow hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
                  <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="bg-primary p-2 rounded-full mb-2 transition-all duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-foreground group-hover:text-foreground/80 transition-colors duration-300 text-center">Recuperação</span>
                  <span className="text-2xl font-bold text-foreground my-2 group-hover:scale-110 transition-transform duration-300">{stats.leadsByStage.recuperacao}</span>
                  <div className="w-full bg-muted rounded-full h-2 mt-1 overflow-hidden">
                    <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.max(5, (stats.leadsByStage.recuperacao / stats.totalLeads) * 100)}%` }}></div>
                  </div>
                  <span className="text-xs font-medium text-foreground mt-1 bg-muted px-2 py-1 rounded-full">{((stats.leadsByStage.recuperacao / stats.totalLeads) * 100).toFixed(1)}%</span>
                </div>
              </div>


              </CardContent>
            </Card>
            
            <Card className="animate-slide-up hover:shadow-lg transition-all duration-300" style={{animationDelay: '0.6s'}}>
              <CardHeader className="bg-accent border-b border-border">
                <div className="flex items-center">
                  <div className="bg-primary h-10 w-10 rounded-xl shadow-lg flex items-center justify-center mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                    </svg>
                  </div>
                  <CardTitle className="text-lg font-bold text-foreground">Status de Agendamentos</CardTitle>
                </div>
              </CardHeader>
              
              <CardContent className="p-5 space-y-6">
                <div className="transform transition-all duration-300 hover:scale-105">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="h-4 w-4 rounded-full bg-primary mr-2"></div>
                      <span className="text-base font-medium text-foreground">Agendados/Confirmados</span>
                    </div>
                    <Badge variant="secondary" className="text-lg font-bold">{stats.appointmentsByStatus.agendado + stats.appointmentsByStatus.confirmado}</Badge>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3 overflow-hidden shadow-inner">
                    <div className="bg-primary h-3 rounded-full transition-all duration-300" style={{ width: `${Math.max(3, ((stats.appointmentsByStatus.agendado + stats.appointmentsByStatus.confirmado) / stats.totalAppointments) * 100)}%` }}></div>
                  </div>
                </div>
                
                <div className="transform transition-all duration-300 hover:scale-105">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="h-4 w-4 rounded-full bg-primary mr-2"></div>
                      <span className="text-base font-medium text-foreground">Realizados</span>
                    </div>
                    <Badge variant="secondary" className="text-lg font-bold">{stats.appointmentsByStatus.realizado}</Badge>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3 overflow-hidden shadow-inner">
                    <div className="bg-primary h-3 rounded-full transition-all duration-300" style={{ width: `${Math.max(3, (stats.appointmentsByStatus.realizado / stats.totalAppointments) * 100)}%` }}></div>
                  </div>
                </div>
                
                <div className="transform transition-all duration-300 hover:scale-105">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="h-4 w-4 rounded-full bg-destructive mr-2"></div>
                      <span className="text-base font-medium text-foreground">Cancelados/Remarcados</span>
                    </div>
                    <Badge variant="destructive" className="text-lg font-bold">{stats.appointmentsByStatus.cancelado + stats.appointmentsByStatus.remarcado}</Badge>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3 overflow-hidden shadow-inner">
                    <div className="bg-destructive h-3 rounded-full transition-all duration-300" style={{ width: `${Math.max(3, ((stats.appointmentsByStatus.cancelado + stats.appointmentsByStatus.remarcado) / stats.totalAppointments) * 100)}%` }}></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Próximos Agendamentos e Leads Recentes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Card Próximos Agendamentos */}
            <Card className="hover:shadow-lg transition-all duration-300 transform hover:scale-[1.01]" style={{animationDelay: '0.7s'}}>
              <CardHeader className="bg-accent border-b border-border flex flex-row items-center justify-between space-y-0 pb-3">
                <div className="flex items-center">
                  <div className="bg-primary h-10 w-10 rounded-xl shadow-lg flex items-center justify-center mr-3">
                    <ClockIcon className="h-5 w-5 text-white" />
                  </div>
                  <CardTitle className="text-lg font-bold text-foreground">Próximos Agendamentos</CardTitle>
                </div>
                <div className="bg-primary p-2 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-white">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </CardHeader>
              
              <CardContent className="p-3">
                {stats.upcomingAppointments.length > 0 ? (
                  <div className="grid gap-4">
                    {stats.upcomingAppointments.map((appointment: any) => (
                      <div
                        key={appointment.id}
                                        className="bg-card shadow-sm border border-border rounded-lg p-4 transition-all duration-300 hover:shadow-md hover:border-primary/20"
                      >
                        <div className="flex items-start space-x-3">
                          {/* Card do dia */}
                          <div className="flex-shrink-0">
                            <div className="bg-accent rounded-xl border border-border shadow-sm h-auto w-20 p-2 flex flex-col items-center justify-center overflow-hidden">
                              <div className="text-primary font-medium text-xs w-full text-center rounded-t-lg bg-primary/10 py-0.5 mb-1">
                                {new Date(appointment.date).toLocaleDateString('pt-BR', { weekday: 'short' }).split('.')[0]}
                              </div>
                              <div className="text-2xl font-bold text-primary mb-0.5 -mt-0.5">
                                {new Date(appointment.date).getDate()}
                              </div>
                              <div className="text-xs uppercase font-medium text-primary tracking-wider">
                                {new Date(appointment.date).toLocaleString('pt-BR', { month: 'short' }).toUpperCase()}
                              </div>
                            </div>
                          </div>
                          
                          {/* Informações principais */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                              <h3 className="text-base font-semibold text-foreground truncate">
                                {appointment.leads?.name || 'Paciente sem nome'}
                              </h3>
                              <Badge 
                                variant={
                                  appointment.status === 'agendado' || appointment.status === 'confirmado' ? 'default' :
                                  appointment.status === 'realizado' ? 'secondary' :
                                  appointment.status === 'cancelado' ? 'destructive' :
                                  'outline'
                                }
                                className="text-xs"
                              >
                                {appointment.status === 'agendado' ? 'Agendado' : 
                                appointment.status === 'confirmado' ? 'Confirmado' :
                                appointment.status === 'realizado' ? 'Realizado' : 
                                appointment.status === 'cancelado' ? 'Cancelado' : 
                                appointment.status === 'remarcado' ? 'Remarcado' : 
                                appointment.status}
                              </Badge>
                            </div>
                            
                            <div className="flex flex-col gap-2.5 mt-3 w-full">
                              <div className="flex items-center">
                                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mr-2.5">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                                  </svg>
                                </div>
                                <div className="text-sm text-foreground bg-muted py-1 px-2 rounded-md border border-border font-medium">
                                  {formatDate(appointment.date)}
                                </div>
                              </div>

                              <div className="flex items-center">
                                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mr-2.5">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <div className="text-sm text-foreground bg-accent py-1 px-2 rounded-md border border-border font-medium">
                                  {appointment.start_time ? appointment.start_time.substring(0, 5) : '-'}
                                  {appointment.end_time ? ` - ${appointment.end_time.substring(0, 5)}` : ''}
                                </div>
                              </div>
                              
                              {appointment.doctors?.name && (
                                <div className="flex items-center w-full">
                                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mr-2.5">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                  </div>
                                  <div className="text-sm text-foreground bg-primary/5 py-1 px-2 rounded-md border border-border font-medium truncate max-w-full">
                                    {appointment.doctors.name}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 px-4">
                    <div className="bg-primary/10 p-3 rounded-full mb-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-foreground text-sm font-medium">Nenhum agendamento próximo</p>
                    <p className="text-muted-foreground text-xs mt-1 text-center">Novos agendamentos aparecerão aqui</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Card Leads Recentes */}
            <Card className="overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-[1.01]" style={{animationDelay: '0.8s'}}>
              <CardHeader className="bg-accent border-b border-border flex flex-row items-center justify-between space-y-0 pb-3">
                <div className="flex items-center">
                  <div className="bg-primary h-10 w-10 rounded-xl shadow-lg flex items-center justify-center mr-3">
                    <UserPlusIcon className="h-5 w-5 text-white" />
                  </div>
                  <CardTitle className="text-lg font-bold text-foreground">Leads Recentes</CardTitle>
                </div>
                <div className="bg-primary p-2 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-white">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </CardHeader>
              
              <CardContent className="p-0">
                {stats.recentLeads.length > 0 ? (
                  <div className="divide-y divide-border">
                    {stats.recentLeads.map((lead: any, index: number) => (
                      <div key={lead.id} className={`p-4 hover:bg-primary/5 transition-all duration-300 cursor-pointer flex items-start ${index === 0 ? 'bg-primary/10' : ''}`}>
                        <div className="flex-shrink-0 mr-4">
                          <div className="bg-primary h-12 w-12 rounded-full shadow-sm flex items-center justify-center text-white font-bold">
                            {lead.name.charAt(0).toUpperCase()}
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold text-foreground truncate">
                              {lead.name}
                            </p>
                            <div className="flex items-center space-x-1">
                              {lead.stage && (
                                <Badge 
                                  variant={
                                    lead.stage === 'acolhimento' ? 'default' : 
                                    lead.stage === 'qualificacao' ? 'default' : 
                                    lead.stage === 'educacao' ? 'secondary' :
                                    lead.stage === 'objecoes' ? 'destructive' :
                                    lead.stage === 'proposta' ? 'outline' :
                                    lead.stage === 'agendamento' ? 'default' :
                                    lead.stage === 'followup' ? 'secondary' :
                                    lead.stage === 'recuperacao' ? 'destructive' :
                                    'outline'
                                  }
                                  className="text-xs"
                                >
                                  {lead.stage === 'acolhimento' ? 'Acolhimento' :
                                   lead.stage === 'qualificacao' ? 'Qualificação' :
                                   lead.stage === 'educacao' ? 'Educação' :
                                   lead.stage === 'objecoes' ? 'Objeções' :
                                   lead.stage === 'proposta' ? 'Proposta' :
                                   lead.stage === 'agendamento' ? 'Agendamento' :
                                   lead.stage === 'followup' ? 'Follow-up' :
                                   lead.stage === 'recuperacao' ? 'Recuperação' :
                                   'Outro'}
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="mt-1.5 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-chart-2 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <p className="text-xs text-muted-foreground truncate">
                              {formatWhatsApp(lead.whatsapp)}
                            </p>
                          </div>
                          
                          <div className="mt-1.5 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-muted-foreground mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-xs text-muted-foreground truncate">
                              {new Date(lead.created_at).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 px-4">
                    <div className="bg-chart-2/10 p-3 rounded-full mb-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-chart-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                    </div>
                    <p className="text-foreground text-sm font-medium">Nenhum lead recente</p>
                    <p className="text-muted-foreground text-xs mt-1 text-center">Novos leads aparecerão aqui</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Gráficos Interativos */}
          <DashboardCharts 
            leadsByStage={{
              acolhimento: stats.leadsByStage?.acolhimento || 0,
              qualificacao: stats.leadsByStage?.qualificacao || 0,
              educacao: stats.leadsByStage?.educacao || 0,
              objecoes: stats.leadsByStage?.objecoes || 0,
              proposta: stats.leadsByStage?.proposta || 0,
              agendamento: stats.leadsByStage?.agendamento || 0,
              followup: stats.leadsByStage?.followup || 0,
              recuperacao: stats.leadsByStage?.recuperacao || 0,
              outros: stats.leadsByStage?.outros || 0
            }}
            appointmentsByStatus={{
              agendado: stats.appointmentsByStatus?.agendado || 0,
              confirmado: stats.appointmentsByStatus?.confirmado || 0,
              realizado: stats.appointmentsByStatus?.realizado || 0,
              cancelado: stats.appointmentsByStatus?.cancelado || 0,
              remarcado: stats.appointmentsByStatus?.remarcado || 0
            }}
            weeklyTrendData={weeklyTrendData}
          />
        </>
      )}
    </div>
  );
};

export default Dashboard;